import ThunderIcon from '../../../assets/icons/pass/thunder.svg';
import Button from '../../../shared/components/Button';
import LineItem from '../../../shared/components/LineItem';
import { usePassProcessor } from '../hooks/usePassProcessor';
import type {
  ProcessedAddOn,
  ProcessedOption,
  RawAddOnProduct,
  RawAttribute,
} from '../hooks/usePassProcessor';

import PassEmptyState from './PassEmptyState';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddToBasketSelection {
  addOns: ProcessedAddOn[];
  options: ProcessedOption[];
}

export interface PassTypesProps {
  rawOptions?: RawAttribute[][];
  rawAddOns?: RawAddOnProduct[];
  options?: ProcessedOption[];
  addOns?: ProcessedAddOn[];
  activeCurrency?: string;
  isMultiOption?: boolean;
  selectedOptions?: string[];
  onOptionChange?: (opts: string[]) => void;
  selectedAddOns?: Record<string, boolean>;
  onSelectionChange?: (id: string, selected: boolean) => void;
  onAddToBasket?: (selection: AddToBasketSelection) => void;
  buttonText?: string;
  disabled?: boolean;
  quantity?: number;
  onQuantityChange?: (qty: number) => void;
  minimal?: boolean;
  className?: string;
  formName?: string;
  passName?: string;
  passPrice?: string;
  passOriginalPrice?: string;
  passDateRange?: string;
  buttonDisabled?: boolean;
  showPassConfigurationText?: boolean;
  hidePassDetails?: boolean;
  showOptions?: boolean;
  showAddOns?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PassTypes = ({
  rawOptions = [],
  rawAddOns = [],
  options: providedOptions = [],
  addOns: providedAddOns = [],
  activeCurrency = 'USD',
  isMultiOption = false,
  selectedOptions = [],
  onOptionChange,
  selectedAddOns = {},
  onSelectionChange,
  onAddToBasket,
  buttonText = 'Add to basket',
  disabled = false,
  quantity,
  onQuantityChange,
  minimal = false,
  className = '',
  formName,
  passName = '',
  passPrice = '',
  passOriginalPrice = '',
  passDateRange = '',
  buttonDisabled = false,
  showPassConfigurationText = false,
  hidePassDetails = false,
  showOptions = true,
  showAddOns = true,
}: PassTypesProps) => {
  // -------------------------------------------------------------------------
  // Data transformation
  // -------------------------------------------------------------------------
  const { options, addOns } = usePassProcessor({
    rawOptions,
    providedOptions,
    rawAddOns,
    providedAddOns,
    activeCurrency,
  });

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------
  const handleCheckboxChange = (addOnId: string, isChecked: boolean) => {
    if (onSelectionChange && !disabled) {
      onSelectionChange(addOnId, isChecked);
    }
  };

  const handleOptionChange = (optionId: string) => {
    if (onOptionChange && !disabled) {
      const option = options.find((o) => o.id === optionId);
      const valToAdd = option?.name || optionId;

      if (isMultiOption) {
        const currentSelected = Array.isArray(selectedOptions) ? selectedOptions : [];
        const isSelected =
          currentSelected.includes(optionId) ||
          (option?.name && currentSelected.includes(option.name));
        if (isSelected) {
          onOptionChange(
            currentSelected.filter((id) => id !== optionId && id !== option?.name)
          );
        } else {
          onOptionChange([...currentSelected, valToAdd]);
        }
      } else {
        onOptionChange([valToAdd]);
      }
    }
  };

  const isOptionSelected = (optionId: string, optionName: string) => {
    const selected = Array.isArray(selectedOptions) ? selectedOptions : [];
    return selected.includes(optionId) || selected.includes(optionName);
  };

  const handleAddToBasket = () => {
    if (onAddToBasket && !disabled) {
      const selectedAddOnItems = addOns.filter((addOn) => selectedAddOns[addOn.id]);
      const selected = Array.isArray(selectedOptions) ? selectedOptions : [];
      const selectedOptionItems = options.filter(
        (opt) => selected.includes(opt.id) || selected.includes(opt.name)
      );
      onAddToBasket({ addOns: selectedAddOnItems, options: selectedOptionItems });
    }
  };

  const hasOptions = options && options.length > 0;
  const hasAddOns = addOns && addOns.length > 0;
  const optionsHeader = 'Options';
  const addOnsHeader = 'Add ons';

  // -------------------------------------------------------------------------
  // Empty state
  // -------------------------------------------------------------------------
  const showEmptyState = quantity === undefined && (!addOns || addOns.length === 0);

  if (showEmptyState) {
    return <PassEmptyState />;
  }

  // -------------------------------------------------------------------------
  // Pass configuration
  // -------------------------------------------------------------------------
  return (
    <div
      className={`${
        minimal
          ? 'w-full p-[16px_16px_0px_16px] border-0 gap-[24px]'
          : 'border border-gray-200 shadow-sm ml-auto w-[440px] p-[32px] border-[1px] gap-[inherit]'
      } bg-white rounded-[8px] overflow-hidden box-border h-auto opacity-100 ${className}`}
    >
      {minimal && !hidePassDetails && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <img src={ThunderIcon} alt="" width="20" height="20" />
            <span className="text-[18px] font-semibold text-[#030407]">
              {passPrice}
            </span>
            {passOriginalPrice && (
              <span className="text-sm text-gray-400 line-through">{passOriginalPrice}</span>
            )}
          </div>

          {passName && (
            <h3 className="mb-1 text-[22px] font-semibold text-[#030407]">
              {passName}
            </h3>
          )}

          {passDateRange && (
            <p className="text-sm text-gray-500 mb-4">
              {passDateRange}
            </p>
          )}

          {quantity !== undefined && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="font-bold text-[18px] text-[#030407]">
                  Total passes
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() =>
                      onQuantityChange && onQuantityChange(Math.max(1, quantity - 1))
                    }
                    disabled={disabled}
                    className="w-6 h-6 text-sm rounded-full text-white flex items-center justify-center hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold bg-[#030407]"
                  >
                    −
                  </button>
                  <div className="w-8 h-8 text-base bg-white border border-gray-200 rounded flex items-center justify-center font-semibold text-blue-900 shadow-sm">
                    {quantity}
                  </div>
                  <button
                    onClick={() =>
                      onQuantityChange && onQuantityChange(quantity + 1)
                    }
                    disabled={disabled}
                    className="w-6 h-6 text-sm rounded-full text-white flex items-center justify-center hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold bg-[#030407]"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {(!minimal || showPassConfigurationText) && (showOptions || showAddOns) && (
        <h2
          className={`text-gray-500 mb-4 tracking-wider text-[17px] font-bold h-5 ${minimal ? 'w-full' : 'w-[376px]'}`}
        >
          Pass configuration
        </h2>
      )}

      {showOptions && hasOptions && (
        <div className={minimal ? 'mb-4' : 'mb-6'}>
          <h3
            className={`${minimal ? 'text-sm font-bold mb-2' : 'text-xl font-bold mb-4'} text-gray-900`}
          >
            {optionsHeader}
          </h3>
          <div className="flex flex-col gap-4">
            {options.map((option) => {
              const selected = isOptionSelected(option.id, option.name);
              return (
                <label key={option.id} className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center mt-1">
                    {isMultiOption ? (
                      <input
                        type="checkbox"
                        name={formName || 'product-option'}
                        checked={selected}
                        onChange={() => handleOptionChange(option.id)}
                        disabled={disabled}
                        className="rounded border-gray-300 text-black focus:ring-0 focus:outline-none accent-black w-4 h-4 border-[#D0D5DD]"
                      />
                    ) : (
                      <>
                        <input
                          type="radio"
                          name={formName || 'product-option'}
                          className="peer sr-only"
                          checked={selected}
                          onChange={() => handleOptionChange(option.id)}
                          disabled={disabled}
                        />
                        <div
                          className={`relative flex items-center justify-center w-4 h-4 rounded-full border-2 transition-all opacity-100 ${selected ? 'bg-black border-black' : 'bg-white border-[#D0D5DD]'}`}
                        >
                          {selected && (
                            <div
                              className="w-1.5 h-1.5 rounded-full bg-white opacity-100"
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`font-bold text-gray-900 ${minimal ? 'text-xs' : 'text-sm'}`}>
                      {option.name}
                    </div>
                    {option.description && (
                      <div className="text-gray-500 text-xs mt-0.5">{option.description}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {showAddOns && hasAddOns && (
        <div className={minimal ? 'mb-4' : 'mb-6'}>
          <h3
            className={`${minimal ? 'text-sm font-bold mb-2' : 'text-xl font-bold mb-4'} text-gray-900`}
          >
            {addOnsHeader}
          </h3>
          <div className="flex flex-col gap-0">
            {addOns.map((addOn) => (
              <LineItem
                key={addOn.id}
                item={addOn}
                variant="selectable"
                selected={!!selectedAddOns[addOn.id]}
                onToggle={handleCheckboxChange}
                currencyCode={addOn.currencyCode}
              />
            ))}
          </div>
        </div>
      )}

      <div className={minimal ? 'mt-2' : 'mt-4'}>
        <Button
          onClick={handleAddToBasket}
          disabled={buttonDisabled}
          className={`w-full ${
            buttonDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-black text-white hover:bg-gray-800'
          } ${minimal ? 'text-sm' : ''} rounded-lg font-bold transition-colors h-10`}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default PassTypes;
